import * as React from 'react'
import './MultiSelectDropdown.css'

export interface GeneratedData {
  name: string
  asset_type: string
  percentage: number
  date?: string
  count?: number | null
}

interface MultiSelectDropdownProps {
  options: GeneratedData[]
  selectedValues: GeneratedData[]
  onChange: (values: GeneratedData[]) => void
  placeholder?: string
  defaultSelectCount?: number
  disabled?: boolean
  maxHeightPx?: number
  className?: string
  minSelection?: number
  maxSelection?: number
}

/** Ensure we only show one row per asset_type */
function uniqueByAssetType (arr: GeneratedData[]): GeneratedData[] {
  const seen = new Set<string>()
  const out: GeneratedData[] = []
  for (const item of arr) {
    if (!seen.has(item.asset_type)) {
      seen.add(item.asset_type)
      out.push(item)
    }
  }
  return out
}

function isSelected (selected: GeneratedData[], item: GeneratedData): boolean {
  return selected.some(s => s.asset_type === item.asset_type)
}

/** Toggle item (by asset_type) */
function toggleItem (selected: GeneratedData[], item: GeneratedData): GeneratedData[] {
  if (isSelected(selected, item)) {
    return selected.filter(s => s.asset_type !== item.asset_type)
  }
  return [...selected, item]
}

export function MultiSelectDropdown ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select...',
  defaultSelectCount = 6,
  disabled = false,
  maxHeightPx = 240,
  className,
  minSelection = 1,
  maxSelection
}: MultiSelectDropdownProps): any {
  const [open, setOpen] = React.useState(false)
  const [activeIndex, setActiveIndex] = React.useState<number>(-1)
  const buttonRef = React.useRef<HTMLButtonElement | null>(null)
  const menuRef = React.useRef<HTMLDivElement | null>(null)

  const hasDefaultedRef = React.useRef(false)

  const uniqueOptions = React.useMemo(() => uniqueByAssetType(options), [options])

  // if minSelection > maxSelection, clamp minSelection down to maxSelection
  const effectiveMin = React.useMemo(() => {
    if (typeof maxSelection === 'number') {
      return Math.min(minSelection, Math.max(0, maxSelection))
    }
    return Math.max(0, minSelection)
  }, [minSelection, maxSelection])

  React.useEffect(() => {
    if (hasDefaultedRef.current) return
    if (uniqueOptions.length === 0) return

    const allowedDefault = typeof maxSelection === 'number'
      ? Math.min(defaultSelectCount ?? 6, uniqueOptions.length, Math.max(0, maxSelection))
      : Math.min(defaultSelectCount ?? 6, uniqueOptions.length)

    const defaults = uniqueOptions.slice(0, allowedDefault)
    if (allowedDefault > 0) {
      onChange(defaults)
      hasDefaultedRef.current = true
    }
  }, [uniqueOptions, defaultSelectCount, onChange, maxSelection])

  const currentlyAtMax = React.useMemo(() => {
    if (typeof maxSelection !== 'number') return false
    return selectedValues.length >= Math.max(0, maxSelection)
  }, [selectedValues.length, maxSelection])

  const onToggle = (opt: GeneratedData): void => {
    const currentlySelected = isSelected(selectedValues, opt)

    // Prevent unselect if it would go below the minimum
    if (currentlySelected && selectedValues.length <= effectiveMin) {
      return
    }

    if (!currentlySelected && currentlyAtMax) {
      return
    }

    const next = toggleItem(selectedValues, opt)
    onChange(next)
  }

  // Close when clicking outside
  React.useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent): void => {
      const target = e.target as Node
      const isInsideMenu = menuRef.current?.contains(target) ?? false
      const isInsideButton = buttonRef.current?.contains(target) ?? false
      if (isInsideMenu || isInsideButton) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => { document.removeEventListener('mousedown', onDocClick) }
  }, [open])

  // Keyboard handlers for button
  const onButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    if (disabled) return
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex(0)
    }
  }

  // Keyboard handlers for menu
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      setOpen(false)
      buttonRef.current?.focus()
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex(i => Math.min((i < 0 ? -1 : i) + 1, uniqueOptions.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex(i => Math.max((i < 0 ? uniqueOptions.length : i) - 1, 0))
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (activeIndex >= 0 && activeIndex < uniqueOptions.length) {
        const opt = uniqueOptions[activeIndex]
        const checked = isSelected(selectedValues, opt)

        // Enforce min/max on keyboard toggle as well
        if (checked && selectedValues.length <= effectiveMin) return
        if (!checked && currentlyAtMax) return
        onToggle(opt)
      }
    }
  }

  const label = React.useMemo(() => {
    if (selectedValues.length === 0) return placeholder
    if (selectedValues.length === 1) return selectedValues[0].asset_type
    return `${selectedValues.length} selected`
  }, [selectedValues, placeholder])

  const title = React.useMemo(
    () => selectedValues.map(v => v.asset_type).join(', '),
    [selectedValues]
  )

  return (
    <div className={`msd-root ${className ?? ''}`}>
      <button
        type="button"
        ref={buttonRef}
        className="msd-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { !disabled && setOpen(o => !o) }}
        onKeyDown={onButtonKeyDown}
        title={title}
      >
        <span className="msd-trigger-label" title={label}>{label}</span>
        <span aria-hidden className="msd-caret">
          <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" className="css-tj5bde-Svg"><path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" style={{ fill: '#fff' }}></path></svg>
        </span>
      </button>

      {open && (
        <div
          ref={menuRef}
          className="msd-menu"
          role="listbox"
          aria-multiselectable="true"
          tabIndex={-1}
          onKeyDown={onMenuKeyDown}
          style={{ maxHeight: `${maxHeightPx}px`, overflow: 'auto' }}
        >
          {uniqueOptions.length === 0 && (
            <div className="msd-empty">No options</div>
          )}

          {uniqueOptions.map((opt, idx) => {
            const checked = isSelected(selectedValues, opt)
            const isActive = idx === activeIndex

            const lockMin = checked && selectedValues.length <= effectiveMin
            const lockMax = !checked && currentlyAtMax
            const isLocked = lockMin || lockMax

            const lockTitle = lockMin
              ? `At least ${effectiveMin} item${effectiveMin > 1 ? 's' : ''} must remain selected`
              : lockMax && typeof maxSelection === 'number'
                ? `You can select at most ${maxSelection} asset${maxSelection > 1 ? 's' : ''}`
                : undefined

            return (
              <div
                key={opt.asset_type}
                className={`msd-option ${isActive ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
                role="option"
                aria-selected={checked}
                aria-disabled={isLocked}
                onMouseEnter={() => { setActiveIndex(idx) }}
                onClick={() => { !isLocked && onToggle(opt) }}
                title={lockTitle}
                style={{
                  opacity: isLocked ? 0.65 : 1,
                  cursor: isLocked ? 'not-allowed' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => {
                    e.stopPropagation()
                    if (!isLocked) onToggle(opt)
                  }}
                  aria-label={opt.asset_type}
                  disabled={isLocked}
                />
                <span className="msd-label" title={opt.asset_type}>{opt.asset_type}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
